import { useAppTheme } from "../../../hooks/useAppTheme";
import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import {
  Modal as RNModal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  Platform,
  View as RNView,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Feather, Ionicons } from "@expo/vector-icons";
import { DataTable } from "react-native-paper";
import { useSelector } from "react-redux";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { View } from "../../../components/shared/Themed";
import Spinner from "../../../components/shared/Spinner";
import { reserveStockModel } from "../../../models/reportsModel";
import { getReserveStocks } from "../../../services/stockService";
import { handleTableLoader } from "../../../utils/common";
import { filterBasedOnAnyKey } from "../../../utils/searchInArrays";
import { sortColumns } from "../../../utils/sortColumns";
import {
  headersReserveStock,
  mediumDeviceHeadersReserveStock,
  smallDeviceHeadersReserveStock,
  mobileDeviceHeadersReserveStock,
} from "../utils/reserveStockHeaders";
import { StockReportDisplaySwitch } from "../StocksReportScreens/utils/StockReportDisplaySwitch";
import { reserveStockTranslationMap } from "../../../utils/translations";
import ReserveStockDetailsScreen from "./ReserveStockDetailsScreen";
import { useTranslation, translateHeaders } from "../../../utils/useTranslation";


function createCardStyles(colors: any, isDark: boolean) {
  return StyleSheet.create({
    cardList: {
      paddingHorizontal: 16,
      paddingBottom: 24,
      paddingTop: 8,
      gap: 12,
      backgroundColor: "transparent",
    },
    tableCard: {
      marginHorizontal: 16,
      marginBottom: 24,
      marginTop: 12,
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      overflow: "hidden" as any,
      shadowColor: isDark ? colors.text : "#0F172A",
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.07,
      shadowRadius: 12,
      elevation: 4,
    },
    card: {
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 16,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E8EDF5",
      shadowColor: isDark ? colors.text : "#1E293B",
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 0.07,
      shadowRadius: 10,
      elevation: 3,
      overflow: "hidden" as any,
      marginBottom: 4,
    },
    cardHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 16,
      paddingVertical: 14,
      backgroundColor: isDark ? colors.surfaceSubtle : "#FAFBFF",
    },
    cardHeaderLeft: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      backgroundColor: "transparent",
      flex: 1,
      paddingRight: 10,
    },
    containerIconWrap: {
      width: 34,
      height: 34,
      borderRadius: 10,
      backgroundColor: isDark ? colors.primary + "33" : "#E1F0FA",
      alignItems: "center",
      justifyContent: "center",
    },
    containerNumber: {
      fontSize: 14,
      fontWeight: "700" as const,
      color: isDark ? colors.text : "#0F172A",
      letterSpacing: 0.2,
      flexWrap: "wrap",
      backgroundColor: "transparent",
    },
    divider: {
      height: 1,
      backgroundColor: isDark ? colors.background : "#f1f5f9",
      marginHorizontal: 16,
    },
    dateRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 16,
      paddingVertical: 14,
      gap: 4,
    },
    dateBlock: {
      flex: 1,
      alignItems: "center",
      gap: 3,
    },
    dateLabel: {
      fontSize: 10,
      color: isDark ? colors.textSecondary : "#94A3B8",
      fontWeight: "600" as const,
      textTransform: "uppercase" as any,
      letterSpacing: 0.5,
    },
    dateValue: {
      fontSize: 13,
      color: isDark ? colors.text : "#1E293B",
      fontWeight: "700" as const,
    },
    cardFooter: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap" as any,
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderTopWidth: 1,
      borderTopColor: isDark ? colors.background : "#f1f5f9",
      backgroundColor: isDark ? colors.surfaceSubtle : "#FAFBFF",
      gap: 8,
    },
    metaChip: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: isDark ? colors.surface : "#FFFFFF",
      borderRadius: 6,
      paddingVertical: 4,
      paddingHorizontal: 8,
      gap: 4,
      borderWidth: 1,
      borderColor: isDark ? colors.border : "#E2E8F0",
      flexShrink: 1,
      maxWidth: "100%",
    },
    metaChipText: {
      fontSize: 11,
      color: isDark ? colors.textSecondary : "#6B7280",
      fontWeight: "500" as const,
      flexShrink: 1,
    },
    emptyState: {
      alignItems: "center",
      paddingVertical: 60,
      gap: 12,
    },
    emptyStateText: {
      fontSize: 15,
      color: "#CBD5E1",
      fontWeight: "500" as const,
    },
  });
}

const MobileReserveStockCard = ({ stock, onPress }: { stock: reserveStockModel, onPress?: () => void }) => {
  const { colors, isDark } = useAppTheme();
  const cardStyles = React.useMemo(() => createCardStyles(colors, isDark), [colors, isDark]);

  const formatShortDate = (dStr: any) => {
    if (!dStr) return "-";
    const date = new Date(dStr);
    if (isNaN(date.getTime())) return String(dStr).split(" ")[0];
    return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
  };

  return (
    <TouchableOpacity style={cardStyles.card} onPress={onPress}>
      <View style={cardStyles.cardHeader}>
        <View style={cardStyles.cardHeaderLeft}>
          <View style={cardStyles.containerIconWrap}>
            <Ionicons name="cube-outline" size={18} color={isDark ? colors.primary : "#0284C7"} />
          </View>
          <Text style={cardStyles.containerNumber} numberOfLines={2} ellipsizeMode="tail">
            {stock.CardName || "-"}
          </Text>
        </View>
      </View>
      <View style={cardStyles.divider} />
      <View style={cardStyles.dateRow}>
        <View style={cardStyles.dateBlock}>
          <Text style={cardStyles.dateLabel}>DOC. NO</Text>
          <Text style={cardStyles.dateValue}>{stock.DocNum || "-"}</Text>
        </View>
        <View style={{ width: 1, height: 24, backgroundColor: isDark ? colors.border : "#E2E8F0" }} />
        <View style={cardStyles.dateBlock}>
          <Text style={cardStyles.dateLabel}>STATUS</Text>
          <Text style={cardStyles.dateValue}>{stock["Doc Status"] || "-"}</Text>
        </View>
        <View style={{ width: 1, height: 24, backgroundColor: isDark ? colors.border : "#E2E8F0" }} />
        <View style={cardStyles.dateBlock}>
          <Text style={cardStyles.dateLabel}>ORDER DATE</Text>
          <Text style={cardStyles.dateValue}>{formatShortDate(stock["Order Date"])}</Text>
        </View>
      </View>
      <View style={cardStyles.divider} />
      <View style={[cardStyles.cardFooter, { justifyContent: "space-between" }]}>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8, flex: 1, backgroundColor: "transparent" }}>
          {stock.WhsName ? (
            <View style={cardStyles.metaChip}>
              <Ionicons name="business-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
              <Text style={cardStyles.metaChipText} numberOfLines={1} ellipsizeMode="tail">{stock.WhsName}</Text>
            </View>
          ) : null}
          {stock.SlpName ? (
            <View style={cardStyles.metaChip}>
              <Ionicons name="person-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
              <Text style={cardStyles.metaChipText} numberOfLines={1} ellipsizeMode="tail">{stock.SlpName}</Text>
            </View>
          ) : null}
          {stock.ItemCode ? (
            <View style={cardStyles.metaChip}>
              <Ionicons name="barcode-outline" size={12} color={isDark ? colors.textSecondary : "#64748B"} />
              <Text style={cardStyles.metaChipText} numberOfLines={1} ellipsizeMode="tail">{stock.ItemCode}</Text>
            </View>
          ) : null}
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default function ReserveStockReportScreen() {

  const navigation = useNavigation<any>();
  const { t } = useTranslation();
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const { colors, isDark } = useAppTheme();
  const styles = React.useMemo(() => createStyles(colors), [colors]);
  const cardStyles = React.useMemo(() => createCardStyles(colors, isDark), [colors, isDark]);
  const columnsToNotHaveThousandSeparator: Array<string> = ["Order Number"];
  const { reserveStockReport, isReserveStockReportListLoaded } = useSelector(
    (state: any) => state.reports
  );

  const [reserveStockPageLevelList, setReserveStockPageLevelList] = useState<
    Array<reserveStockModel>
  >([]);
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [searchedText, setSearchedText] = useState<string>("");
  const [tableHeaders, setTableHeaders] = useState<
    Record<string, Record<string, string | number | boolean>>
  >(headersReserveStock);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [_isDisplayViewOn, _setIsDisplayViewOn] = useState<boolean>(false);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({
    activeKey: null,
    overKey: null,
  });
  const [mobilePage, setMobilePage] = useState<number>(0);
  const [detailsModalVisible, setDetailsModalVisible] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<reserveStockModel | null>(null);
  const [settingsBtnPos, setSettingsBtnPos] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const settingsBtnRef = useRef<any>(null);

  useEffect(() => {
    AsyncStorage.getItem("reserve_stock_report_hidden_columns").then(
      (stored) => {
        if (stored) {
          try {
            setHiddenColumns(JSON.parse(stored));
          } catch { }
        }
      }
    );
    AsyncStorage.getItem("reserve_stock_report_column_order").then((stored) => {
      if (stored) {
        try {
          setColumnOrder(JSON.parse(stored));
        } catch { }
      }
    });
  }, []);

  const getAllHeaders = () => {
    return isLargeDevice
      ? headersReserveStock
      : isMediumDevice
        ? mediumDeviceHeadersReserveStock
        : isMobileDevice
          ? mobileDeviceHeadersReserveStock
          : smallDeviceHeadersReserveStock;
  };

  const getOrderedKeys = useCallback(
    (baseHeaders: Record<string, any>) => {
      const allKeys = Object.keys(baseHeaders);
      if (columnOrder.length === 0) return allKeys;

      const newKeys: string[] = [];
      columnOrder.forEach((k) => {
        if (allKeys.includes(k) && !newKeys.includes(k)) newKeys.push(k);
      });
      allKeys.forEach((k) => {
        if (!newKeys.includes(k)) newKeys.push(k);
      });
      return newKeys;
    },
    [columnOrder]
  );

  const getFilteredHeaders = (
    baseHeaders: Record<string, Record<string, string | number | boolean>>
  ) => {
    const orderedKeys = getOrderedKeys(baseHeaders);
    const visibleEntries = orderedKeys
      .filter((key) => !hiddenColumns.includes(key))
      .map((key) => [key, baseHeaders[key]]);

    const totalOriginalFlex = Object.values(baseHeaders).reduce(
      (sum, header) => sum + (Number((header as any).width) || 0),
      0
    );
    const totalVisibleFlex = visibleEntries.reduce(
      (sum, [, header]) => sum + (Number((header as any).width) || 0),
      0
    );
    const scale = totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<string, Record<string, string | number | boolean>> = {};
    visibleEntries.forEach(([key, value]) => {
      result[key as string] = { ...(value as any), width: Number((value as any).width) * scale };
    });
    return result;
  };

  useEffect(() => {
    const allHeaders = getAllHeaders();
    setTableHeaders(
      translateHeaders(getFilteredHeaders(allHeaders), t, reserveStockTranslationMap)
    );
  }, [hiddenColumns, columnOrder, isLargeDevice, isMediumDevice, isSmallDevice, isMobileDevice, t]);

  const moveColumn = useCallback(
    (fromKey: string, toKey: string) => {
      const allHeaders = getAllHeaders();
      const currentKeys = getOrderedKeys(allHeaders).filter((k) => k !== "");
      const fromIdx = currentKeys.indexOf(fromKey);
      const toIdx = currentKeys.indexOf(toKey);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
      const newKeys = [...currentKeys];
      newKeys.splice(fromIdx, 1);
      newKeys.splice(toIdx, 0, fromKey);
      newKeys.push("");
      setColumnOrder(newKeys);
      AsyncStorage.setItem("reserve_stock_report_column_order", JSON.stringify(newKeys));
    },
    [columnOrder, getOrderedKeys]
  );

  const resetColumnOrder = () => {
    setColumnOrder([]);
    AsyncStorage.removeItem("reserve_stock_report_column_order");
  };

  const startColumnDrag = useCallback(
    (key: string) => {
      if (Platform.OS !== "web" || typeof window === "undefined") return;
      dragRef.current.activeKey = key;
      setDragActiveKey(key);

      const onMouseMove = (e: MouseEvent) => {
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const row =
          el && (el as HTMLElement).closest
            ? (el as HTMLElement).closest("[data-columnkey]")
            : null;
        const overKey = row ? row.getAttribute("data-columnkey") : null;
        if (overKey !== dragRef.current.overKey) {
          dragRef.current.overKey = overKey;
          setDragOverKey(
            overKey && overKey !== dragRef.current.activeKey ? overKey : null
          );
        }
      };

      const onMouseUp = () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        (document.body.style as any).userSelect = "";
        const from = dragRef.current.activeKey;
        const to = dragRef.current.overKey;
        if (from && to && from !== to) {
          moveColumn(from, to);
        }
        dragRef.current = { activeKey: null, overKey: null };
        setDragActiveKey(null);
        setDragOverKey(null);
      };

      document.body.style.cursor = "grabbing";
      (document.body.style as any).userSelect = "none";
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [moveColumn]
  );

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((key) => key !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem(
        "reserve_stock_report_hidden_columns",
        JSON.stringify(updated)
      );
      return updated;
    });
  };

  const displayTableLoader = () => {
    handleTableLoader(true, false, setIsSearched);
  };

  const fadeTableLoader = () => {
    handleTableLoader(false, true, setIsSearched);
  };

  const displayReserveStockByCarton = (
    reserveStocks: Array<reserveStockModel>
  ) => {
    return reserveStocks.map((reserveStock: reserveStockModel) => ({
      ...reserveStock,
      "Open Qty PCS":
        Number(reserveStock["Open Qty PCS"]) /
        (Number(reserveStock.PerCtns) || 1),
    }));
  };

  const getDisplayedReserveStocks = (
    reserveStocks: Array<reserveStockModel>
  ) => {
    return _isDisplayViewOn
      ? displayReserveStockByCarton(reserveStocks)
      : reserveStocks;
  };

  const onHandleSortColumn = (columnKey: string) => {
    const sortedList: Array<reserveStockModel> = sortColumns(
      reserveStockPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders
    );

    setReserveStockPageLevelList(sortedList);
  };

  const handleSearch = (searchText: string) => {
    displayTableLoader();
    setSearchedText(searchText);

    if (searchText.trim() !== "") {
      const searchedList: Array<reserveStockModel> = filterBasedOnAnyKey(
        reserveStockReport,
        searchText
      );
      setReserveStockPageLevelList(getDisplayedReserveStocks(searchedList));
    } else {
      setReserveStockPageLevelList(
        getDisplayedReserveStocks(reserveStockReport)
      );
    }

    setTimeout(() => {
      fadeTableLoader();
    }, 500);
  };

  useEffect(() => {
    let isMounted = true;

    if (isMounted && (reserveStockPageLevelList.length === 0 || isRefresh)) {
      displayTableLoader();
      getReserveStocks(tableHeaders);
    }

    const allHeaders = getAllHeaders();
    setTableHeaders(
      translateHeaders(getFilteredHeaders(allHeaders), t, reserveStockTranslationMap)
    );
    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isRefresh, t]);

  useEffect(() => {
    const filteredReserveStocks =
      searchedText.trim() !== ""
        ? filterBasedOnAnyKey(reserveStockReport, searchedText)
        : reserveStockReport;

    setReserveStockPageLevelList(
      getDisplayedReserveStocks(filteredReserveStocks)
    );

    setTimeout(() => {
      fadeTableLoader();
    }, 1000);
  }, [reserveStockReport, _isDisplayViewOn]);

  const navigateToReportDetails = (order: any, action: string) => {
    setSelectedOrder(order);
    setDetailsModalVisible(true);
  };

  return (
    <View style={{ flex: 1, backgroundColor: isDark ? colors.background : "#eef2f6" }}>
      <ScrollView style={{ flex: 1, backgroundColor: isDark ? colors.background : "#eef2f6" }} contentContainerStyle={{ flexGrow: 1, backgroundColor: isDark ? colors.background : "#eef2f6" }}>
        <View
          style={[
            styles.pageWrapper,
            { backgroundColor: columnSettingsOpen ? colors.primaryBg : (isDark ? colors.background : "#eef2f6") }
          ]}
        >
            {isReserveStockReportListLoaded ? (
              <>
              <View style={[cardStyles.tableCard, !(isSmallDevice || isMobileDevice) && { marginTop: 12 }, (isSmallDevice || isMobileDevice) && { marginBottom: 8 }]}>
                <LabelBannerWithSearchBar
                  titleName={`${t("totalItems")}: ${reserveStockPageLevelList.length}`}
                  handleSearch={handleSearch}
                  setRefresh={setRefresh}

                  //Export Data to Excel
                  dataToExport={reserveStockPageLevelList.map((reserveStock) => {
                    const pieces = _isDisplayViewOn
                      ? Number(reserveStock["Open Qty PCS"]) * (Number(reserveStock.PerCtns) || 1)
                      : Number(reserveStock["Open Qty PCS"]);
                    const cartons = _isDisplayViewOn
                      ? Number(reserveStock["Open Qty PCS"])
                      : Number(reserveStock["Open Qty PCS"]) / (Number(reserveStock.PerCtns) || 1);

                    const formatToDDMMYYYY = (dStr: any) => {
                      if (!dStr) return "";
                      const date = new Date(dStr);
                      if (isNaN(date.getTime())) return String(dStr).split(" ")[0];
                      return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
                    };

                    const formatNumber = (val: any) => {
                      if (val === null || val === undefined || val === "") return val;
                      const num = Number(val);
                      return isNaN(num) ? val : num.toLocaleString("en-US", { maximumFractionDigits: 2 });
                    };

                    return {
                      "Order Number": reserveStock.DocNum,
                      "Customer Code": reserveStock.CardCode,
                      "Customer Name": reserveStock.CardName,
                      "Address": reserveStock.Address2,
                      "Warehouse": reserveStock.WhsName,
                      "Item Code": reserveStock.ItemCode,
                      "Description": reserveStock.Dscription,
                      "Order Reserved (Pieces)": formatNumber(pieces),
                      "Order Reserved (Cartons)": formatNumber(cartons),
                      "Sales": reserveStock.SlpName,
                      "Order Date": formatToDDMMYYYY(reserveStock["Order Date"]),
                      "Valid until": formatToDDMMYYYY(reserveStock["Valid until"]),
                      "Doc Status": reserveStock["Doc Status"],
                    };
                  })}
                  fileName="reserveStockReport"
                  sheetName="Reserve Stock Report"

                  preSearchControls={(
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                      {!(isSmallDevice || isMobileDevice) && (
                        <StockReportDisplaySwitch
                          title={t("display")}
                          isDisplayViewOn={_isDisplayViewOn}
                          setIsDisplayViewOn={_setIsDisplayViewOn}
                        />
                      )}
                      {!isMobileDevice && (
                        <TouchableOpacity
                          ref={settingsBtnRef}
                          style={[styles.settingsButton, { backgroundColor: columnSettingsOpen ? colors.primary + "1A" : colors.surfaceSubtle, borderColor: columnSettingsOpen ? colors.primary : colors.border }]}
                          onPress={() => {
                            if (settingsBtnRef.current) {
                              settingsBtnRef.current.measureInWindow(
                                (
                                  x: number,
                                  y: number,
                                  width: number,
                                  height: number
                                ) => {
                                  setSettingsBtnPos({
                                    x: x || 0,
                                    y: y || 0,
                                    width: width || 0,
                                    height: height || 0,
                                  });
                                  setColumnSettingsOpen(!columnSettingsOpen);
                                }
                              );
                            } else {
                              setColumnSettingsOpen(!columnSettingsOpen);
                            }
                          }}
                        >
                          <Feather
                            name="settings"
                            size={15}
                            color={columnSettingsOpen ? colors.primary : colors.textSecondary}
                          />
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                />
                {!(isSmallDevice || isMobileDevice) && (
                  <DataTableWithPagination
                    headers={tableHeaders}
                    rows={reserveStockPageLevelList}
                    itemsPerPage={100}
                    useModal={false}
                    isDetail={isMobileDevice}
                    setModalVisible={() => { }}
                    setSelected={() => { }}
                    isDataLoading={isSearched}
                    columnsToNotHaveThousandSeparator={
                      columnsToNotHaveThousandSeparator
                    }
                    handleRowClick={() => { }}
                    handleDeleteRow={() => { }}
                    handleSortColumn={onHandleSortColumn}
                    selectable={true}
                    operationActions={navigateToReportDetails}
                  />
                )}
                {columnSettingsOpen && (
                  <RNModal
                    transparent={true}
                    visible={columnSettingsOpen}
                    animationType="fade"
                    onRequestClose={() => setColumnSettingsOpen(false)}
                  >
                    <Pressable
                      style={styles.overlay}
                      onPress={() => setColumnSettingsOpen(false)}
                    >
                      <Pressable
                        style={[
                          styles.menu,
                          {
                            top: settingsBtnPos.y + settingsBtnPos.height + 6,
                            left: settingsBtnPos.x - 180 + 40,
                          },
                        ]}
                        onPress={(event) => event.stopPropagation()}
                      >
                        <Text style={styles.menuTitle}>{t("showHideColumns")}</Text>
                        <View style={styles.menuDivider} />
                        <TouchableOpacity
                          style={styles.menuRow}
                          onPress={() => {
                            const allKeys = Object.keys(getAllHeaders()).filter(
                              (key) => key !== ""
                            );
                            if (hiddenColumns.length === 0) {
                              const updated = [...allKeys];
                              setHiddenColumns(updated);
                              AsyncStorage.setItem(
                                "reserve_stock_report_hidden_columns",
                                JSON.stringify(updated)
                              );
                            } else {
                              setHiddenColumns([]);
                              AsyncStorage.setItem(
                                "reserve_stock_report_hidden_columns",
                                JSON.stringify([])
                              );
                            }
                          }}
                        >
                          <View
                            style={[
                              styles.checkbox,
                              hiddenColumns.length === 0 && styles.checkboxActive,
                            ]}
                          >
                            {hiddenColumns.length === 0 && (
                              <Feather name="check" size={13} color={colors.surface} />
                            )}
                          </View>
                          <Text style={styles.menuRowLabel}>{t("selectAll")}</Text>
                        </TouchableOpacity>
                        <View style={styles.menuDivider} />
                        <ScrollView
                          style={{ maxHeight: 300 }}
                          showsVerticalScrollIndicator={true}
                        >
                          {getOrderedKeys(getAllHeaders()).map((key) => {
                            if (key === "") return null;
                            const value = getAllHeaders()[key];
                            if (!value) return null;
                            const isVisible = !hiddenColumns.includes(key);
                            const isDragging = dragActiveKey === key;
                            const isDragOver = dragOverKey === key;
                            return (
                              <RNView
                                key={key}
                                {...({
                                  dataSet: { columnkey: key },
                                } as any)}
                                style={{
                                  flexDirection: "row",
                                  alignItems: "center",
                                  paddingVertical: 8,
                                  paddingHorizontal: 16,
                                  opacity: isDragging ? 0.35 : 1,
                                  backgroundColor: isDragOver
                                    ? "#EFF6FF"
                                    : "transparent",
                                  borderTopWidth: isDragOver ? 2 : 0,
                                  borderTopColor: "#56B7FD",
                                }}
                              >
                                <Pressable
                                  onPressIn={() => startColumnDrag(key)}
                                  style={
                                    {
                                      padding: 4,
                                      marginRight: 6,
                                      cursor: "grab",
                                    } as any
                                  }
                                >
                                  <Feather
                                    name="menu"
                                    size={14}
                                    color="#9CA3AF"
                                  />
                                </Pressable>
                                <TouchableOpacity
                                  style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    flex: 1,
                                  }}
                                  onPress={() => toggleColumn(key)}
                                >
                                  <RNView
                                    style={[
                                      styles.checkbox,
                                      isVisible && styles.checkboxActive,
                                      { marginRight: 10 }
                                    ]}
                                  >
                                    {isVisible && (
                                      <Feather name="check" size={13} color={colors.surface} />
                                    )}
                                  </RNView>
                                  <Text
                                    style={[
                                      styles.columnLabel,
                                      !isVisible && styles.columnLabelHidden,
                                    ]}
                                  >
                                    {(value as any).name}
                                  </Text>
                                </TouchableOpacity>
                              </RNView>
                            );
                          })}
                        </ScrollView>
                        {columnOrder.length > 0 && (
                          <>
                            <RNView
                              style={{
                                height: 1,
                                backgroundColor: colors.borderSubtle,
                                marginTop: 4,
                              }}
                            />
                            <TouchableOpacity
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                paddingVertical: 10,
                                paddingHorizontal: 16,
                              }}
                              onPress={resetColumnOrder}
                            >
                              <Feather
                                name="rotate-ccw"
                                size={13}
                                color={colors.textSecondary}
                              />
                              <Text
                                style={{
                                  fontSize: 13,
                                  color: colors.textSecondary,
                                  fontWeight: "600",
                                  marginLeft: 6,
                                }}
                              >
                                {t("resetOrder") || "Reset Order"}
                              </Text>
                            </TouchableOpacity>
                          </>
                        )}
                      </Pressable>
                    </Pressable>
                  </RNModal>
                )}
              </View>

              {(isSmallDevice || isMobileDevice) && (
                <View style={cardStyles.cardList}>
                  {reserveStockPageLevelList.length === 0 ? (
                    <View style={cardStyles.emptyState}>
                      <Ionicons name="cube-outline" size={48} color="#CBD5E1" />
                      <Text style={cardStyles.emptyStateText}>{t("noDataAvailable")}</Text>
                    </View>
                  ) : (
                    <>
                      {reserveStockPageLevelList
                        .slice(mobilePage * 100, (mobilePage + 1) * 100)
                        .map((stock, index) => (
                          <MobileReserveStockCard
                            key={`${stock.DocNum || "stock"}-${index}`}
                            stock={stock}
                            onPress={() => {
                              setSelectedOrder(stock);
                              setDetailsModalVisible(true);
                            }}
                          />
                        ))}
                      <DataTable.Pagination
                        page={mobilePage}
                        numberOfPages={Math.ceil(reserveStockPageLevelList.length / 100)}
                        onPageChange={(page) => setMobilePage(page)}
                        label={`${reserveStockPageLevelList.length === 0 ? 0 : mobilePage * 100 + 1}-${Math.min(
                          (mobilePage + 1) * 100,
                          reserveStockPageLevelList.length,
                        )} of ${reserveStockPageLevelList.length}`}
                        showFastPaginationControls
                      />
                    </>
                  )}
                </View>
              )}
              </>
            ) : (
              <View>
                <Spinner
                  size="small"
                  color="#74BBFF"
                  styles={{ flexDirection: "row", margin: "20%" }}
                />
              </View>
            )}
        </View>
      </ScrollView>
      {detailsModalVisible && selectedOrder && (
        <ReserveStockDetailsScreen
          order={selectedOrder}
          onBackPress={() => {
            setDetailsModalVisible(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </View>
  );
}

const createStyles = (colors: any) => StyleSheet.create({
  pageWrapper: {
    flex: 1,
    minHeight: "100%" as any,
  },
  settingsButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: colors.surfaceSubtle,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: colors.border,
  },
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.15)",
  },
  menu: {
    position: "absolute",
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: 10,
    minWidth: 220,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 10,
  },
  menuTitle: {
    fontSize: 12,
    fontWeight: "700",
    color: colors.textSecondary,
    paddingHorizontal: 16,
    paddingBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  menuDivider: {
    height: 1,
    backgroundColor: colors.borderSubtle,
    marginBottom: 4,
  },
  menuRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  menuRowLabel: {
    fontSize: 14,
    color: colors.text,
    fontWeight: "700",
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 10,
  },
  checkboxActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
  columnLabel: {
    fontSize: 14,
    color: colors.text,
    fontWeight: "600",
  },
  columnLabelHidden: {
    color: colors.textMuted,
    fontWeight: "400",
  },
});